'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';

import {
  LoadingSplashScreen,
  LoginForm,
  StyledHeader,
  StyledTextCall,
} from '~/entities/Login';
import { RecoveryPasswordModalForm } from '~/entities/RecoveryPassword/components';
import { Dialog, isStaging } from '~/shared';
import { DecodedMartinsToken } from '~/types';

import { AuthProviders } from '../types/providers';

function getExpiresInMs(t?: string) {
  if (!t) return null;
  const martinsToken = jwtDecode<DecodedMartinsToken>(t);
  return martinsToken.exp * 1000 - Date.now();
}

type MartinsTokenRefreshSessionProps = {
  st?: string;
};

export function MartinsTokenRefreshSession({
  st,
}: MartinsTokenRefreshSessionProps) {
  const session = useSession();
  const queryClient = useQueryClient();

  const exp = getExpiresInMs(session.data?.t.st ?? st);

  const [isSubmitting, setIsSubmitting] = useState<`${AuthProviders}` | null>(
    null
  );

  const [isMartinsTokenRefreshing, setIsMartinsTokenRefreshing] = useState(
    exp ? exp < 0 : false
  );

  const [currentStep, setCurrentStep] = useState<
    'credentials' | 'mfa' | 'recovery-email'
  >('credentials');

  function onLoginSuccess() {
    queryClient.refetchQueries();
    setIsMartinsTokenRefreshing(false);
    setIsSubmitting(null);
    setCurrentStep('credentials');
  }

  function handleStepChange(step: 'credentials' | 'mfa') {
    setCurrentStep(step);
  }

  useEffect(() => {
    if (!exp) return () => {};

    if (exp < 0) {
      setIsMartinsTokenRefreshing(true);
      return () => {};
    }

    const expireTimeout = setTimeout(() => {
      setIsMartinsTokenRefreshing(true);
    }, exp);

    return () => clearTimeout(expireTimeout);
  }, [exp]);

  if (!st) return null;

  return !isMartinsTokenRefreshing ? null : (
    <>
      {!!isSubmitting && <LoadingSplashScreen isSubmitting={isSubmitting} />}
      <Dialog open maxWidth="sm" withCloseButton={false}>
        {currentStep === 'credentials' && (
          <>
            <StyledHeader sx={{ textAlign: 'center' }} variant="h1">
              Sua sessão Martins expirou
            </StyledHeader>
            <StyledTextCall sx={{ textAlign: 'center' }}>
              {isStaging()
                ? 'Faça login novamente com seu usuário e senha para continuar.'
                : 'No seu dispositivo inclua o código do MFA e aprove a entrada'}
            </StyledTextCall>
          </>
        )}
        {!isStaging() && currentStep === 'mfa' && (
          <>
            <StyledHeader sx={{ textAlign: 'center' }} variant="h1">
              Registre seu MFA para continuar
            </StyledHeader>
            <StyledTextCall sx={{ textAlign: 'center' }}>
              Digite o seu codigo MFA
            </StyledTextCall>
          </>
        )}
        {currentStep === 'recovery-email' && (
          <>
            <StyledHeader sx={{ textAlign: 'center' }} variant="h1">
              Recuperação de senha
            </StyledHeader>
            <StyledTextCall sx={{ textAlign: 'center' }}>
              Informe seu e-mail para resetar a senha.
            </StyledTextCall>
          </>
        )}
        <Box sx={{ maxWidth: '420px', margin: '0 auto', marginTop: 2, p: 2 }}>
          {currentStep === 'recovery-email' ? (
            <RecoveryPasswordModalForm
              onBack={() => setCurrentStep('credentials')}
            />
          ) : (
            <LoginForm
              onLoginSuccess={onLoginSuccess}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              onStepChange={isStaging() ? undefined : handleStepChange}
              onRecoveryRequest={() => setCurrentStep('recovery-email')}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}
